/**
 * Component Registry System
 *
 * This file provides a centralized registry of all page components
 * with clear mapping to their routes and domains.
 */

import React from 'react';
import { ArtifactTree } from '../components/Artifacts/ArtifactTree';
import { ArtifactTreeSelection } from '../components/Artifacts/ArtifactTreeSelection';
import CompanyArtifactLayout from '../components/Artifacts/CompanyArtifactLayout';
import CompanyArtifactsIndex from '../components/Artifacts/CompanyArtifactsIndex';
import CompanyArtifactsSelectTreeRedirect from '../components/Artifacts/CompanyArtifactsSelectTreeRedirect';
import { Home } from '../pages/Home';
import { RegisterPage } from '../pages/RegisterPage';
import { LoginPage } from '../pages/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { ProspectorChatPage } from '../pages/onboarding/ProspectorChatPage';
import { OnboardingPaywallPage } from '../pages/onboarding/OnboardingPaywallPage';
import { FoundationConfirmationPage } from '../pages/onboarding/FoundationConfirmationPage';
import { FitfinderCatalogPage } from '../pages/fitfinder/FitfinderCatalogPage';
import { FitfinderSessionPage } from '../pages/fitfinder/FitfinderSessionPage';
import { PartnerHome } from '../pages/PartnerHome';
import { PartnerDeferredEnrollmentPage } from '../pages/partner/PartnerDeferredEnrollmentPage';
import { ResourcesLibraryPage } from '../pages/resources/ResourcesLibraryPage';
import { ResourceArticlePage } from '../pages/resources/ResourceArticlePage';
import { ResourcesAdminPage } from '../pages/admin/ResourcesAdminPage';
import { KeyedLandingPage } from '../pages/KeyedLandingPage';
import { ResilienceCircleLandingPage } from '../pages/ResilienceCircleLandingPage';
import { ArtifactTypeList } from '../components/ArtifactTypes/ArtifactTypeList';
import ParameterTypeList from '../components/ParameterTypes/ParameterTypeList';
import PrimeModelLayout from '../components/PrimeModel/PrimeModelLayout';
import ProcessPageManager from '../components/PrimeModel/ProcessPage/ProcessPageManager';
import ArtifactProcessPage from '../components/Artifacts/ArtifactProcessPage';
import { CompanyProcessPage } from '../components/CompanyProcess';
import ArtifactTransitionTracer from '../components/PrimeModel/ArtifactTransitionTracer';
import GPTChat from '../components/GPTIntegration/GPTChat';
import { NoRootPrompt } from '../components/Artifacts/NoRootPrompt';
import ArchitectureAgentPage from '../pages/ArchitectureAgentPage';
import DevelopmentPage from '../pages/DevelopmentPage';
import CompanyModelRenderSurfacePage from '../components/RenderSurface/CompanyModelRenderSurfacePage';
import ControlBridgePage from '../components/ControlBridge/ControlBridgePage';
import UnifiedHubControlBridgePage from '../components/ControlBridge/UnifiedHubControlBridgePage';
import DashboardLegacyRedirect from '../components/routing/DashboardLegacyRedirect';

// Additional imports for missing components
import { ArtifactDetails } from '../components/Artifacts/ArtifactDetails';
import { ArtifactTabs } from '../components/Artifacts/ArtifactTabs';
import { UnifiedStateManager } from '../components/Artifacts/UnifiedStateManager';
import { UnifiedProcessManager } from '../components/Artifacts/UnifiedProcessManager';
import { UnifiedTransitionManager } from '../components/Artifacts/UnifiedTransitionManager';
import { UnifiedTransitionForm } from '../components/Artifacts/UnifiedTransitionForm';
import { StateForm } from '../components/Artifacts/StateForm';
import { StateManagement } from '../components/Artifacts/StateManagement';
import { RecommendedArtifacts } from '../components/Artifacts/RecommendedArtifacts';
import PrimeModelManager from '../components/PrimeModel/PrimeModelManager';
import PrimeArtifactDetails from '../components/PrimeModel/PrimeArtifactDetails';
import { PrimeStateManager } from '../components/PrimeModel/PrimeStateManager';
import PrimeTransitionManager from '../components/PrimeModel/PrimeTransitionManager';
import PrimeTransitionForm from '../components/PrimeModel/PrimeTransitionForm';
import { PrimeModelCreation } from '../components/PrimeModel/PrimeModelCreation';
import PrimeModelSummary from '../components/PrimeModel/PrimeModelSummary';
import TransitionCreationFlow from '../components/PrimeModel/TransitionCreationFlow';
import { StateTransitionGenerator } from '../components/PrimeModel/StateTransitionGenerator';
import { ItemManager } from '../components/PrimeModel/ItemManager';
import { ProcessForm } from '../components/PrimeModel/ProcessForm';
import ProcessPage from '../components/PrimeModel/ProcessPage/ProcessPage';
import ArtifactCreationDialog from '../components/PrimeModel/ArtifactCreationDialog';
import { RelationshipVisualizationPage } from '../components/PrimeModel/RelationshipVisualization/RelationshipVisualizationPage';
import GPTGenerationTab from '../components/PrimeModel/GPTGenerationTab/GPTGenerationTab';
import { ArchitectureAgent } from '../components/ArchitectureAgent/ArchitectureAgent';
import JsonFieldEditor from '../components/common/JsonFieldEditor';
import TreeNode from '../components/TreeNode/TreeNode';
import { ErrorBoundary } from '../components/ErrorBoundary';
import ApiHealthCheck from '../components/ApiHealthCheck';
import { NestedStateMachineViewer } from '../components/NestedStateMachineViewer';
import { ProcessDefinitionPage } from '../components/ProcessDefinition/ProcessDefinitionPage';
import { ArtifactItemDashboard } from '../components/Dashboard/ArtifactItemDashboard';
import DevelopmentSummary from '../components/Development/DevelopmentSummary';
import { ArtifactGraph } from '../components/ArtifactGraph/ArtifactGraph';
import Header from '../components/Header/Header';
import { ChatOperationalLayout } from '../components/ChatOperational';
import { UserManagement } from '../components/UserManagement';
import { CompanyManagement } from '../components/CompanyManagement';
import { CompanyInstanceManagement } from '../components/CompanyManagement/CompanyInstanceManagement';
import { CompanyDetailPage } from '../components/CompanyManagement/CompanyDetailPage';
import { SubscriptionManagement } from '../components/SubscriptionManagement/SubscriptionManagement';
import { SubscriptionTypesManagement } from '../components/SubscriptionManagement/SubscriptionTypesManagement';
import { PlatformSubscriptionCatalogPage } from '../components/SubscriptionManagement/PlatformSubscriptionCatalogPage';
import DemoLayout from '../components/Demo/DemoLayout';
import DemoPageFallback from '../pages/DemoPageFallback';
import { DemoPage } from '../pages/DemoPage';
import UnifiedHeader from '../components/Header/UnifiedHeader';
import { SkillsPage } from '../pages/SkillsPage';
import { StateTemplateLibraryManager } from '../components/StateTemplates/StateTemplateLibraryManager';
import { KrybotDashboard } from '../components/Krybot';
import { OptionCatalogManagementPage } from '../pages/OptionCatalogManagementPage';
import { ArtifactTreeManager } from '../components/ArtifactTreeManager/ArtifactTreeManager';
import { ArtifactTreeManagerPage } from '../components/ArtifactTreeManager/ArtifactTreeManagerPage';
import { GlobalVariableManagementPage } from '../pages/GlobalVariableManagementPage';
import { GlobalPropertiesManagementPage } from '../pages/GlobalPropertiesManagementPage';
import { HierarchicalDashboard } from '../pages/HierarchicalDashboard';
import { BridgeSessionPage } from '../pages/BridgeSession/BridgeSessionPage';
import { BridgeSessionReportPage } from '../pages/BridgeSession/BridgeSessionReportPage';
import { ClientModelClientPage } from '../pages/ClientModel/ClientModelClientPage';
import { UpcomingSessionsPage } from '../pages/UpcomingSessionsPage';
import { CompanyAdminUsersPage } from '../components/CompanyAdmin/CompanyAdminUsersPage';
import { CompanyAdminSubscriptionsPage } from '../components/CompanyAdmin/CompanyAdminSubscriptionsPage';
import { CompanyAdminModelsPage } from '../components/CompanyAdmin/CompanyAdminModelsPage';
import { CompanyAdminBillingPage } from '../components/CompanyAdmin/CompanyAdminBillingPage';
import { CompanyAdminSessionsPage } from '../components/CompanyAdmin/CompanyAdminSessionsPage';
import { BridgeSessionSchedulePage } from '../pages/BridgeSessionSchedulePage';
import { LocalBridgePage } from '../pages/LocalBridgePage';
import { LocalBridgeUsersPage } from '../pages/LocalBridgeUsersPage';
import { LocalBridgeCompaniesPage } from '../pages/LocalBridgeCompaniesPage';
import { LocalBridgeCompanyTransactionsPage } from '../pages/LocalBridgeCompanyTransactionsPage';
import { LocalBridgeMarketFitPage } from '../pages/LocalBridgeMarketFitPage';
import { BusinessObjectivePage } from '../pages/BusinessObjectivePage';
import { BusinessObjectiveWorkshopPage } from '../pages/BusinessObjectiveWorkshopPage';
import { MarketFitGuidedFlowPage } from '../pages/MarketFitGuidedFlowPage';
import { MarketFitSituationPage } from '../pages/MarketFitSituationPage';
import { MarketFitSolutionPage } from '../pages/MarketFitSolutionPage';
import { ProductDomainBridgeSessionPage } from '../pages/ProductDomainBridgeSessionPage';
import { ControlBridgeDomainSelectorPage } from '../pages/ControlBridgeDomainSelectorPage';
import { BridgeMigrationPage } from '../pages/BridgeMigrationPage';

// ============================================================================
// COMPONENT REGISTRY
// ============================================================================

export interface ComponentInfo {
  name: string;
  component: React.ComponentType<any>;
  domain: 'company' | 'prime' | 'shared' | 'admin';
  description: string;
  filePath: string;
  dependencies?: string[];
  props?: Record<string, unknown>;
}

export const COMPONENT_REGISTRY: Record<string, ComponentInfo> = {
  // ============================================================================
  // COMPANY ARTIFACTS (c-artifacts) - Actual Business Instances
  // ============================================================================

  CompanyArtifactLayout: {
    name: 'CompanyArtifactLayout',
    component: CompanyArtifactLayout,
    domain: 'company',
    description: 'Company artifacts page layout with tabs (Artifact Tree, Workflow, Processes)',
    filePath: 'src/components/Artifacts/CompanyArtifactLayout.tsx',
    dependencies: [
      'ArtifactTree',
      'WorkflowDashboard',
      'ProcessCentricView',
    ],
  },

  CompanyArtifactsIndex: {
    name: 'CompanyArtifactsIndex',
    component: CompanyArtifactsIndex,
    domain: 'company',
    description: 'Redirects /artifacts to /artifacts/artifact-tree',
    filePath: 'src/components/Artifacts/CompanyArtifactsIndex.tsx',
  },

  CompanyArtifactsSelectTreeRedirect: {
    name: 'CompanyArtifactsSelectTreeRedirect',
    component: CompanyArtifactsSelectTreeRedirect,
    domain: 'company',
    description: 'Legacy redirect from /artifacts/select-tree',
    filePath: 'src/components/Artifacts/CompanyArtifactsSelectTreeRedirect.tsx',
  },

  ArtifactTree: {
    name: 'ArtifactTree',
    component: ArtifactTree,
    domain: 'company',
    description: 'Main company artifacts tree component - displays actual business instances',
    filePath: 'src/components/Artifacts/ArtifactTree.tsx',
    dependencies: [
      'ArtifactTabs',
      'UnifiedStateManager',
      'UnifiedProcessManager',
      'UnifiedTransitionManager',
    ],
  },

  ArtifactTreeSelection: {
    name: 'ArtifactTreeSelection',
    component: ArtifactTreeSelection,
    domain: 'company',
    description: 'Company artifact tree selection component',
    filePath: 'src/components/Artifacts/ArtifactTreeSelection.tsx',
  },

  NoRootPrompt: {
    name: 'NoRootPrompt',
    component: NoRootPrompt,
    domain: 'company',
    description: 'Component for creating root company artifacts',
    filePath: 'src/components/Artifacts/NoRootPrompt.tsx',
  },

  ArtifactProcessPage: {
    name: 'ArtifactProcessPage',
    component: ArtifactProcessPage,
    domain: 'company',
    description: 'Company artifact processes management page',
    filePath: 'src/components/Artifacts/ArtifactProcessPage.tsx',
  },

  CompanyProcessPage: {
    name: 'CompanyProcessPage',
    component: CompanyProcessPage,
    domain: 'company',
    description: 'Company process management page using common ProcessTask components',
    filePath: 'src/components/CompanyProcess/CompanyProcessPage.tsx',
    dependencies: [
      'ProcessTaskManager',
      'ProcessArtifactManager',
      'ProcessRoleManager',
      'ProcessValidationPanel',
    ],
  },

  // ============================================================================
  // PRIME ARTIFACTS (p-artifacts) - Templates/Models
  // ============================================================================

  PrimeModelLayout: {
    name: 'PrimeModelLayout',
    component: PrimeModelLayout,
    domain: 'prime',
    description: 'Main prime artifacts layout - templates and models',
    filePath: 'src/components/PrimeModel/PrimeModelLayout.tsx',
    dependencies: ['PrimeModelManager', 'PrimeArtifactDetails'],
  },

  ArtifactTransitionTracer: {
    name: 'ArtifactTransitionTracer',
    component: ArtifactTransitionTracer,
    domain: 'prime',
    description: 'Prime artifact transition tracer component',
    filePath: 'src/components/PrimeModel/ArtifactTransitionTracer.tsx',
    props: { artifactId: '', artifactName: '' },
  },

  ProcessPageManager: {
    name: 'ProcessPageManager',
    component: ProcessPageManager,
    domain: 'prime',
    description: 'Prime process relationships manager',
    filePath: 'src/components/PrimeModel/ProcessPage/ProcessPageManager.tsx',
  },

  // ============================================================================
  // SHARED/ADMIN COMPONENTS
  // ============================================================================

  ArtifactTypeList: {
    name: 'ArtifactTypeList',
    component: ArtifactTypeList,
    domain: 'admin',
    description: 'Artifact type definitions management',
    filePath: 'src/components/ArtifactTypes/ArtifactTypeList.tsx',
  },

  ParameterTypeList: {
    name: 'ParameterTypeList',
    component: ParameterTypeList,
    domain: 'admin',
    description: 'Parameter type definitions management',
    filePath: 'src/components/ParameterTypes/ParameterTypeList.tsx',
  },

  GPTChat: {
    name: 'GPTChat',
    component: GPTChat,
    domain: 'shared',
    description: 'GPT integration and assistance component',
    filePath: 'src/components/GPTIntegration/GPTChat.tsx',
  },

  ArchitectureAgentPage: {
    name: 'ArchitectureAgentPage',
    component: ArchitectureAgentPage,
    domain: 'shared',
    description: 'Architecture agent tools page',
    filePath: 'src/pages/ArchitectureAgentPage.tsx',
  },

  DevelopmentPage: {
    name: 'DevelopmentPage',
    component: DevelopmentPage,
    domain: 'admin',
    description: 'Development tools and utilities page',
    filePath: 'src/pages/DevelopmentPage.tsx',
  },

  CompanyModelRenderSurfacePage: {
    name: 'CompanyModelRenderSurfacePage',
    component: CompanyModelRenderSurfacePage,
    domain: 'shared',
    description: 'Read-only company-model render surface (kryspace manifest consumer)',
    filePath: 'src/components/RenderSurface/CompanyModelRenderSurfacePage.tsx',
  },

  ControlBridgePage: {
    name: 'ControlBridgePage',
    component: ControlBridgePage,
    domain: 'shared',
    description: 'Manifest-backed Control Bridge surfaces (design handoff Phase 3)',
    filePath: 'src/components/ControlBridge/ControlBridgePage.tsx',
  },

  UnifiedHubControlBridgePage: {
    name: 'UnifiedHubControlBridgePage',
    component: UnifiedHubControlBridgePage,
    domain: 'shared',
    description: 'Unified /hub cross-domain Control Bridge landing',
    filePath: 'src/components/ControlBridge/UnifiedHubControlBridgePage.tsx',
  },

  DashboardLegacyRedirect: {
    name: 'DashboardLegacyRedirect',
    component: DashboardLegacyRedirect,
    domain: 'company',
    description: 'Redirects legacy /dashboard root to manifest-backed render Control Bridge',
    filePath: 'src/components/routing/DashboardLegacyRedirect.tsx',
  },

  Home: {
    name: 'Home',
    component: Home,
    domain: 'shared',
    description: 'Application home page component',
    filePath: 'src/pages/Home.tsx',
  },

  RegisterPage: {
    name: 'RegisterPage',
    component: RegisterPage,
    domain: 'shared',
    description: 'Account registration and post-signup verification messaging',
    filePath: 'src/pages/RegisterPage.tsx',
  },

  LoginPage: {
    name: 'LoginPage',
    component: LoginPage,
    domain: 'shared',
    description: 'Sign in with optional next redirect',
    filePath: 'src/pages/LoginPage.tsx',
  },

  ForgotPasswordPage: {
    name: 'ForgotPasswordPage',
    component: ForgotPasswordPage,
    domain: 'shared',
    description: 'Request password reset email',
    filePath: 'src/pages/ForgotPasswordPage.tsx',
  },

  ResetPasswordPage: {
    name: 'ResetPasswordPage',
    component: ResetPasswordPage,
    domain: 'shared',
    description: 'Set new password with token from email',
    filePath: 'src/pages/ResetPasswordPage.tsx',
  },

  ProspectorChatPage: {
    name: 'ProspectorChatPage',
    component: ProspectorChatPage,
    domain: 'shared',
    description: 'Intake prospector (Krybot) chat',
    filePath: 'src/pages/onboarding/ProspectorChatPage.tsx',
  },

  OnboardingPaywallPage: {
    name: 'OnboardingPaywallPage',
    component: OnboardingPaywallPage,
    domain: 'shared',
    description: 'Onboarding paywall — catalog bundle selection and Stripe Checkout',
    filePath: 'src/pages/onboarding/OnboardingPaywallPage.tsx',
  },

  FoundationConfirmationPage: {
    name: 'FoundationConfirmationPage',
    component: FoundationConfirmationPage,
    domain: 'shared',
    description: 'Post-foundation booking confirmation',
    filePath: 'src/pages/onboarding/FoundationConfirmationPage.tsx',
  },

  FitfinderCatalogPage: {
    name: 'FitfinderCatalogPage',
    component: FitfinderCatalogPage,
    domain: 'shared',
    description: 'FitFinder product-solution catalog (partner)',
    filePath: 'src/pages/fitfinder/FitfinderCatalogPage.tsx',
  },

  FitfinderSessionPage: {
    name: 'FitfinderSessionPage',
    component: FitfinderSessionPage,
    domain: 'shared',
    description: 'FitFinder prospect session lifecycle + Stripe return',
    filePath: 'src/pages/fitfinder/FitfinderSessionPage.tsx',
  },

  VerifyEmailPage: {
    name: 'VerifyEmailPage',
    component: VerifyEmailPage,
    domain: 'shared',
    description: 'Email verification callback from link',
    filePath: 'src/pages/VerifyEmailPage.tsx',
  },

  PartnerHome: {
    name: 'PartnerHome',
    component: PartnerHome,
    domain: 'shared',
    description: 'Partner landing page component',
    filePath: 'src/pages/PartnerHome.tsx',
  },

  KeyedLandingPage: {
    name: 'KeyedLandingPage',
    component: KeyedLandingPage,
    domain: 'shared',
    description: 'Magnet / partner keyed landing consuming source_key',
    filePath: 'src/pages/KeyedLandingPage.tsx',
  },

  ResilienceCircleLandingPage: {
    name: 'ResilienceCircleLandingPage',
    component: ResilienceCircleLandingPage,
    domain: 'shared',
    description: 'Resilience Circle Five-Pillars assessment lead-capture surface',
    filePath: 'src/pages/ResilienceCircleLandingPage.tsx',
  },

  ResourcesLibraryPage: {
    name: 'ResourcesLibraryPage',
    component: ResourcesLibraryPage,
    domain: 'shared',
    description: 'Articles / blogs resources library (guest + auth)',
    filePath: 'src/pages/resources/ResourcesLibraryPage.tsx',
  },

  ResourceArticlePage: {
    name: 'ResourceArticlePage',
    component: ResourceArticlePage,
    domain: 'shared',
    description: 'Single resource article / explainer detail',
    filePath: 'src/pages/resources/ResourceArticlePage.tsx',
  },

  ResourcesAdminPage: {
    name: 'ResourcesAdminPage',
    component: ResourcesAdminPage,
    domain: 'admin',
    description: 'Platform admin Resources authoring / upload',
    filePath: 'src/pages/admin/ResourcesAdminPage.tsx',
  },

  PartnerDeferredEnrollmentPage: {
    name: 'PartnerDeferredEnrollmentPage',
    component: PartnerDeferredEnrollmentPage,
    domain: 'shared',
    description: 'PARTNER_DEFERRED subscription enrollment (no Stripe at create)',
    filePath: 'src/pages/partner/PartnerDeferredEnrollmentPage.tsx',
  },

  // ============================================================================
  // ADDITIONAL COMPANY ARTIFACT COMPONENTS
  // ============================================================================

  ArtifactDetails: {
    name: 'ArtifactDetails',
    component: ArtifactDetails,
    domain: 'company',
    description: 'Company artifact details component',
    filePath: 'src/components/Artifacts/ArtifactDetails.tsx',
  },

  ArtifactTabs: {
    name: 'ArtifactTabs',
    component: ArtifactTabs,
    domain: 'company',
    description: 'Company artifact tabs component',
    filePath: 'src/components/Artifacts/ArtifactTabs.tsx',
  },

  UnifiedStateManager: {
    name: 'UnifiedStateManager',
    component: UnifiedStateManager,
    domain: 'company',
    description: 'Unified state manager for company artifacts',
    filePath: 'src/components/Artifacts/UnifiedStateManager.tsx',
  },

  UnifiedProcessManager: {
    name: 'UnifiedProcessManager',
    component: UnifiedProcessManager,
    domain: 'company',
    description: 'Unified process manager for company artifacts',
    filePath: 'src/components/Artifacts/UnifiedProcessManager.tsx',
  },

  UnifiedTransitionManager: {
    name: 'UnifiedTransitionManager',
    component: UnifiedTransitionManager,
    domain: 'company',
    description: 'Unified transition manager for company artifacts',
    filePath: 'src/components/Artifacts/UnifiedTransitionManager.tsx',
  },

  UnifiedTransitionForm: {
    name: 'UnifiedTransitionForm',
    component: UnifiedTransitionForm,
    domain: 'company',
    description: 'Unified transition form for company artifacts',
    filePath: 'src/components/Artifacts/UnifiedTransitionForm.tsx',
  },

  StateForm: {
    name: 'StateForm',
    component: StateForm,
    domain: 'company',
    description: 'State form for company artifacts',
    filePath: 'src/components/Artifacts/StateForm.tsx',
  },

  StateManagement: {
    name: 'StateManagement',
    component: StateManagement,
    domain: 'company',
    description: 'State management for company artifacts',
    filePath: 'src/components/Artifacts/StateManagement.tsx',
  },

  RecommendedArtifacts: {
    name: 'RecommendedArtifacts',
    component: RecommendedArtifacts,
    domain: 'company',
    description: 'Recommended artifacts for company instances',
    filePath: 'src/components/Artifacts/RecommendedArtifacts.tsx',
  },

  // ============================================================================
  // ADDITIONAL PRIME ARTIFACT COMPONENTS
  // ============================================================================

  PrimeModelManager: {
    name: 'PrimeModelManager',
    component: PrimeModelManager,
    domain: 'prime',
    description: 'Prime model manager component',
    filePath: 'src/components/PrimeModel/PrimeModelManager.tsx',
  },

  PrimeArtifactDetails: {
    name: 'PrimeArtifactDetails',
    component: PrimeArtifactDetails,
    domain: 'prime',
    description: 'Prime artifact details component',
    filePath: 'src/components/PrimeModel/PrimeArtifactDetails.tsx',
  },

  PrimeStateManager: {
    name: 'PrimeStateManager',
    component: PrimeStateManager,
    domain: 'prime',
    description: 'Prime state manager component',
    filePath: 'src/components/PrimeModel/PrimeStateManager.tsx',
  },

  PrimeTransitionManager: {
    name: 'PrimeTransitionManager',
    component: PrimeTransitionManager,
    domain: 'prime',
    description: 'Prime transition manager component',
    filePath: 'src/components/PrimeModel/PrimeTransitionManager.tsx',
  },

  PrimeTransitionForm: {
    name: 'PrimeTransitionForm',
    component: PrimeTransitionForm,
    domain: 'prime',
    description: 'Prime transition form component',
    filePath: 'src/components/PrimeModel/PrimeTransitionForm.tsx',
  },

  PrimeModelCreation: {
    name: 'PrimeModelCreation',
    component: PrimeModelCreation,
    domain: 'prime',
    description: 'Prime model creation component',
    filePath: 'src/components/PrimeModel/PrimeModelCreation.tsx',
  },

  PrimeModelSummary: {
    name: 'PrimeModelSummary',
    component: PrimeModelSummary,
    domain: 'prime',
    description: 'Prime model summary component',
    filePath: 'src/components/PrimeModel/PrimeModelSummary.tsx',
  },

  TransitionCreationFlow: {
    name: 'TransitionCreationFlow',
    component: TransitionCreationFlow,
    domain: 'prime',
    description: 'Prime transition creation flow component',
    filePath: 'src/components/PrimeModel/TransitionCreationFlow.tsx',
  },

  StateTransitionGenerator: {
    name: 'StateTransitionGenerator',
    component: StateTransitionGenerator,
    domain: 'prime',
    description: 'Prime state transition generator component',
    filePath: 'src/components/PrimeModel/StateTransitionGenerator.tsx',
  },

  ItemManager: {
    name: 'ItemManager',
    component: ItemManager,
    domain: 'prime',
    description: 'Prime item manager component',
    filePath: 'src/components/PrimeModel/ItemManager.tsx',
  },

  ProcessForm: {
    name: 'ProcessForm',
    component: ProcessForm,
    domain: 'prime',
    description: 'Prime process form component',
    filePath: 'src/components/PrimeModel/ProcessForm.tsx',
  },

  ProcessPage: {
    name: 'ProcessPage',
    component: ProcessPage,
    domain: 'prime',
    description: 'Prime process page component',
    filePath: 'src/components/PrimeModel/ProcessPage/ProcessPage.tsx',
  },

  ArtifactCreationDialog: {
    name: 'ArtifactCreationDialog',
    component: ArtifactCreationDialog,
    domain: 'prime',
    description: 'Prime artifact creation dialog component',
    filePath: 'src/components/PrimeModel/ArtifactCreationDialog.tsx',
  },

  RelationshipVisualizationPage: {
    name: 'RelationshipVisualizationPage',
    component: RelationshipVisualizationPage,
    domain: 'prime',
    description: 'Prime relationship visualization page component',
    filePath:
      'src/components/PrimeModel/RelationshipVisualization/RelationshipVisualizationPage.tsx',
  },

  GPTGenerationTab: {
    name: 'GPTGenerationTab',
    component: GPTGenerationTab,
    domain: 'prime',
    description: 'Prime GPT generation tab component',
    filePath: 'src/components/PrimeModel/GPTGenerationTab/GPTGenerationTab.tsx',
  },

  // ============================================================================
  // ADDITIONAL SHARED COMPONENTS
  // ============================================================================

  ArchitectureAgent: {
    name: 'ArchitectureAgent',
    component: ArchitectureAgent,
    domain: 'shared',
    description: 'Architecture agent component',
    filePath: 'src/components/ArchitectureAgent/ArchitectureAgent.tsx',
  },

  JsonFieldEditor: {
    name: 'JsonFieldEditor',
    component: JsonFieldEditor,
    domain: 'shared',
    description: 'JSON field editor component',
    filePath: 'src/components/common/JsonFieldEditor.tsx',
  },

  TreeNode: {
    name: 'TreeNode',
    component: TreeNode,
    domain: 'shared',
    description: 'Tree node component',
    filePath: 'src/components/TreeNode/TreeNode.tsx',
  },

  ErrorBoundary: {
    name: 'ErrorBoundary',
    component: ErrorBoundary,
    domain: 'shared',
    description: 'Error boundary component',
    filePath: 'src/components/ErrorBoundary.tsx',
  },

  ApiHealthCheck: {
    name: 'ApiHealthCheck',
    component: ApiHealthCheck,
    domain: 'shared',
    description: 'API health check component',
    filePath: 'src/components/ApiHealthCheck.tsx',
  },

  NestedStateMachineViewer: {
    name: 'NestedStateMachineViewer',
    component: NestedStateMachineViewer,
    domain: 'shared',
    description: 'Nested state machine viewer component',
    filePath: 'src/components/NestedStateMachineViewer.tsx',
  },

  Header: {
    name: 'Header',
    component: Header,
    domain: 'shared',
    description: 'Application header component',
    filePath: 'src/components/Header/Header.tsx',
  },

  // ============================================================================
  // ADDITIONAL ADMIN COMPONENTS
  // ============================================================================

  ProcessDefinitionPage: {
    name: 'ProcessDefinitionPage',
    component: ProcessDefinitionPage,
    domain: 'admin',
    description: 'Process definition page component',
    filePath: 'src/components/ProcessDefinition/ProcessDefinitionPage.tsx',
  },

  ArtifactItemDashboard: {
    name: 'ArtifactItemDashboard',
    component: ArtifactItemDashboard,
    domain: 'admin',
    description: 'Artifact item dashboard component',
    filePath: 'src/components/Dashboard/ArtifactItemDashboard.tsx',
  },

  DevelopmentSummary: {
    name: 'DevelopmentSummary',
    component: DevelopmentSummary,
    domain: 'admin',
    description: 'Development summary component',
    filePath: 'src/components/Development/DevelopmentSummary.tsx',
  },

  ArtifactGraph: {
    name: 'ArtifactGraph',
    component: ArtifactGraph,
    domain: 'admin',
    description: 'Artifact graph component',
    filePath: 'src/components/ArtifactGraph/ArtifactGraph.tsx',
  },

  ChatOperationalLayout: {
    name: 'ChatOperationalLayout',
    component: ChatOperationalLayout,
    domain: 'shared',
    description: 'Chat-based operational interface for artifact management',
    filePath: 'src/components/ChatOperational/ChatOperationalLayout.tsx',
  },

  UserManagement: {
    name: 'UserManagement',
    component: UserManagement,
    domain: 'admin',
    description: 'User management and administration interface',
    filePath: 'src/components/UserManagement/UserManagement.tsx',
  },

  CompanyManagement: {
    name: 'CompanyManagement',
    component: CompanyManagement,
    domain: 'admin',
    description: 'Company management and administration interface',
    filePath: 'src/components/CompanyManagement/CompanyManagement.tsx',
  },

  CompanyDetailPage: {
    name: 'CompanyDetailPage',
    component: CompanyDetailPage,
    domain: 'admin',
    description: 'Company detail page with tabs for profile, users, subscriptions, and billing',
    filePath: 'src/components/CompanyManagement/CompanyDetailPage.tsx',
  },

  SubscriptionManagement: {
    name: 'SubscriptionManagement',
    component: SubscriptionManagement,
    domain: 'admin',
    description: 'Subscription management and administration interface',
    filePath: 'src/components/SubscriptionManagement/SubscriptionManagement.tsx',
  },

  PlatformSubscriptionCatalogPage: {
    name: 'PlatformSubscriptionCatalogPage',
    component: PlatformSubscriptionCatalogPage,
    domain: 'admin',
    description: 'Read-only subscription catalog. Create, update, and delete are refused on this page.',
    filePath: 'src/components/SubscriptionManagement/PlatformSubscriptionCatalogPage.tsx',
  },

  SubscriptionTypesManagement: {
    name: 'SubscriptionTypesManagement',
    component: SubscriptionTypesManagement,
    domain: 'admin',
    description: 'Subscription types and features management interface',
    filePath: 'src/components/SubscriptionManagement/SubscriptionTypesManagement.tsx',
  },

  CompanyInstanceManagement: {
    name: 'CompanyInstanceManagement',
    component: CompanyInstanceManagement,
    domain: 'admin',
    description: 'Company instance management interface',
    filePath: 'src/components/CompanyManagement/CompanyInstanceManagement.tsx',
  },

  DemoLayout: {
    name: 'DemoLayout',
    component: DemoLayout,
    domain: 'shared',
    description: 'Interactive demo experience layout',
    filePath: 'src/components/Demo/DemoLayout.tsx',
  },

  DemoPageFallback: {
    name: 'DemoPageFallback',
    component: DemoPageFallback,
    domain: 'shared',
    description: 'Fallback demo page without context provider',
    filePath: 'src/pages/DemoPageFallback.tsx',
  },

  DemoPage: {
    name: 'DemoPage',
    component: DemoPage,
    domain: 'shared',
    description: 'Main demo page with context provider',
    filePath: 'src/pages/DemoPage.tsx',
  },

  UnifiedHeader: {
    name: 'UnifiedHeader',
    component: UnifiedHeader,
    domain: 'shared',
    description: 'Unified header component with user controls and navigation',
    filePath: 'src/components/Header/UnifiedHeader.tsx',
  },

  SkillsPage: {
    name: 'SkillsPage',
    component: SkillsPage,
    domain: 'admin',
    description: 'Skill taxonomy management page',
    filePath: 'src/pages/SkillsPage.tsx',
  },

  StateTemplateLibraryManager: {
    name: 'StateTemplateLibraryManager',
    component: StateTemplateLibraryManager,
    domain: 'admin',
    description: 'State template library management for reusable state machine templates',
    filePath: 'src/components/StateTemplates/StateTemplateLibraryManager.tsx',
  },

  KrybotDashboard: {
    name: 'KrybotDashboard',
    component: KrybotDashboard,
    domain: 'admin',
    description: 'Krybot agent management dashboard - manage agents, templates, and prompts',
    filePath: 'src/components/Krybot/KrybotDashboard.tsx',
  },

  OptionCatalogManagementPage: {
    name: 'OptionCatalogManagementPage',
    component: OptionCatalogManagementPage,
    domain: 'admin',
    description: 'Option catalog management - create and manage global option catalogs',
    filePath: 'src/pages/OptionCatalogManagementPage.tsx',
  },

  ArtifactTreeManager: {
    name: 'ArtifactTreeManager',
    component: ArtifactTreeManager,
    domain: 'admin',
    description: 'Artifact tree management and administration interface',
    filePath: 'src/components/ArtifactTreeManager/ArtifactTreeManager.tsx',
  },

  ArtifactTreeManagerPage: {
    name: 'ArtifactTreeManagerPage',
    component: ArtifactTreeManagerPage,
    domain: 'admin',
    description: 'Artifact tree manager page wrapper',
    filePath: 'src/components/ArtifactTreeManager/ArtifactTreeManagerPage.tsx',
  },

  GlobalVariableManagementPage: {
    name: 'GlobalVariableManagementPage',
    component: GlobalVariableManagementPage,
    domain: 'admin',
    description: 'Global variable management - manage company-wide and system-wide variables',
    filePath: 'src/pages/GlobalVariableManagementPage.tsx',
  },

  GlobalPropertiesManagementPage: {
    name: 'GlobalPropertiesManagementPage',
    component: GlobalPropertiesManagementPage,
    domain: 'admin',
    description: 'Global properties management - manage property definitions, relationships, and option catalogs',
    filePath: 'src/pages/GlobalPropertiesManagementPage.tsx',
  },

  // ============================================================================
  // HIERARCHICAL DASHBOARD
  // ============================================================================

  HierarchicalDashboard: {
    name: 'HierarchicalDashboard',
    component: HierarchicalDashboard,
    domain: 'company',
    description: 'Control Bridge with drill-down navigation through business, product, operations, stakeholders, and customers',
    filePath: 'src/pages/HierarchicalDashboard/HierarchicalDashboard.tsx',
    dependencies: [
      'DashboardBreadcrumb',
      'DashboardQuickNav',
      'DashboardFavorites',
      'BusinessDashboard',
      'ProductDashboard',
      'OperationsDashboard',
      'StakeholdersDashboard',
      'CustomersDashboard',
    ],
  },

  BridgeSessionReportPage: {
    name: 'BridgeSessionReportPage',
    component: BridgeSessionReportPage,
    domain: 'company',
    description: 'Full customer report: entropy heat map, top 10 friction, condition worksheets, bridge card, session analysis (print/PDF)',
    filePath: 'src/pages/BridgeSession/BridgeSessionReportPage.tsx',
    dependencies: ['BridgeCard'],
  },

  BridgeSessionPage: {
    name: 'BridgeSessionPage',
    component: BridgeSessionPage,
    domain: 'company',
    description: 'Bridge Session flow for entropy reduction - guides users from domain definition through entropy reduction to Control Bridge execution',
    filePath: 'src/pages/BridgeSession/BridgeSessionPage.tsx',
    dependencies: [
      'BridgeSessionLayout',
      'LeftProgressSidebar',
      'DomainBuilder',
      'EntropyHeatMap',
      'BridgeCard',
    ],
  },

  ClientModelClientPage: {
    name: 'ClientModelClientPage',
    component: ClientModelClientPage,
    domain: 'company',
    description: 'Client model snapshot, priorities, history — reports §5.2 + projection (ISSUE-221/222)',
    filePath: 'src/pages/ClientModel/ClientModelClientPage.tsx',
  },

  UpcomingSessionsPage: {
    name: 'UpcomingSessionsPage',
    component: UpcomingSessionsPage,
    domain: 'shared',
    description: 'User Bridge Session bookings list with change-time',
    filePath: 'src/pages/UpcomingSessionsPage.tsx',
  },

  CompanyAdminUsersPage: {
    name: 'CompanyAdminUsersPage',
    component: CompanyAdminUsersPage,
    domain: 'company',
    description: 'Selected-company roster and membership roles',
    filePath: 'src/components/CompanyAdmin/CompanyAdminUsersPage.tsx',
  },

  CompanyAdminSubscriptionsPage: {
    name: 'CompanyAdminSubscriptionsPage',
    component: CompanyAdminSubscriptionsPage,
    domain: 'company',
    description: 'Selected-company plans and seat assignment',
    filePath: 'src/components/CompanyAdmin/CompanyAdminSubscriptionsPage.tsx',
  },

  CompanyAdminModelsPage: {
    name: 'CompanyAdminModelsPage',
    component: CompanyAdminModelsPage,
    domain: 'company',
    description: 'Selected-company artifact trees',
    filePath: 'src/components/CompanyAdmin/CompanyAdminModelsPage.tsx',
  },

  CompanyAdminBillingPage: {
    name: 'CompanyAdminBillingPage',
    component: CompanyAdminBillingPage,
    domain: 'company',
    description: 'Selected-company billing account',
    filePath: 'src/components/CompanyAdmin/CompanyAdminBillingPage.tsx',
  },

  CompanyAdminSessionsPage: {
    name: 'CompanyAdminSessionsPage',
    component: CompanyAdminSessionsPage,
    domain: 'company',
    description: 'Selected-company session bookings',
    filePath: 'src/components/CompanyAdmin/CompanyAdminSessionsPage.tsx',
  },

  LocalBridgePage: {
    name: 'LocalBridgePage',
    component: LocalBridgePage,
    domain: 'shared',
    description: 'Local Bridge hub: create user/company/session, execute or view',
    filePath: 'src/pages/LocalBridgePage.tsx',
  },

  LocalBridgeUsersPage: {
    name: 'LocalBridgeUsersPage',
    component: LocalBridgeUsersPage,
    domain: 'shared',
    description: 'Local Bridge: manage users in the local database',
    filePath: 'src/pages/LocalBridgeUsersPage.tsx',
  },

  LocalBridgeCompaniesPage: {
    name: 'LocalBridgeCompaniesPage',
    component: LocalBridgeCompaniesPage,
    domain: 'shared',
    description: 'Local Bridge: manage companies in the local database',
    filePath: 'src/pages/LocalBridgeCompaniesPage.tsx',
  },

  LocalBridgeCompanyTransactionsPage: {
    name: 'LocalBridgeCompanyTransactionsPage',
    component: LocalBridgeCompanyTransactionsPage,
    domain: 'shared',
    description: 'Local Bridge: view and manage company transactions (Revenue, Expense, Sales)',
    filePath: 'src/pages/LocalBridgeCompanyTransactionsPage.tsx',
  },

  BridgeMigrationPage: {
    name: 'BridgeMigrationPage',
    component: BridgeMigrationPage,
    domain: 'admin',
    description: 'Bridge migration management - match required artifact types to production',
    filePath: 'src/pages/BridgeMigrationPage.tsx',
  },

  LocalBridgeMarketFitPage: {
    name: 'LocalBridgeMarketFitPage',
    component: LocalBridgeMarketFitPage,
    domain: 'shared',
    description: 'Local Bridge MarketFit: deals, products, situations for a session',
    filePath: 'src/pages/LocalBridgeMarketFitPage.tsx',
  },

  BusinessObjectivePage: {
    name: 'BusinessObjectivePage',
    component: BusinessObjectivePage,
    domain: 'shared',
    description: 'Business domain: collect objective and current state (legacy)',
    filePath: 'src/pages/BusinessObjectivePage.tsx',
  },

  BusinessObjectiveWorkshopPage: {
    name: 'BusinessObjectiveWorkshopPage',
    component: BusinessObjectiveWorkshopPage,
    domain: 'shared',
    description: 'Business Objective Workshop: 5-phase flow',
    filePath: 'src/pages/BusinessObjectiveWorkshopPage.tsx',
  },

  MarketFitGuidedFlowPage: {
    name: 'MarketFitGuidedFlowPage',
    component: MarketFitGuidedFlowPage,
    domain: 'shared',
    description: 'MarketFit Guided Flow: state-machine prompt-to-schema session',
    filePath: 'src/pages/MarketFitGuidedFlowPage.tsx',
  },

  MarketFitSituationPage: {
    name: 'MarketFitSituationPage',
    component: MarketFitSituationPage,
    domain: 'company',
    description: 'MarketFit Situation Definition: form + agent for assets, needs, inputs, outputs',
    filePath: 'src/pages/MarketFitSituationPage.tsx',
  },

  MarketFitSolutionPage: {
    name: 'MarketFitSolutionPage',
    component: MarketFitSolutionPage,
    domain: 'company',
    description: 'MarketFit Solution Definition: form + agent for inputs and benefits',
    filePath: 'src/pages/MarketFitSolutionPage.tsx',
  },

  ProductDomainBridgeSessionPage: {
    name: 'ProductDomainBridgeSessionPage',
    component: ProductDomainBridgeSessionPage,
    domain: 'shared',
    description: 'Product Domain Bridge Session: questions, entropy heat map, Bridge Card',
    filePath: 'src/pages/ProductDomainBridgeSessionPage.tsx',
  },

  ControlBridgeDomainSelectorPage: {
    name: 'ControlBridgeDomainSelectorPage',
    component: ControlBridgeDomainSelectorPage,
    domain: 'shared',
    description: 'Control Bridge domain selection (post-workshop)',
    filePath: 'src/pages/ControlBridgeDomainSelectorPage.tsx',
  },

  BridgeSessionSchedulePage: {
    name: 'BridgeSessionSchedulePage',
    component: BridgeSessionSchedulePage,
    domain: 'admin',
    description: 'Admin Bridge Session schedule - all bookings',
    filePath: 'src/pages/BridgeSessionSchedulePage.tsx',
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get component info by name
 */
export const getComponentInfo = (name: string): ComponentInfo | undefined => {
  return COMPONENT_REGISTRY[name];
};

/**
 * Get all components for a specific domain
 */
export const getComponentsByDomain = (
  domain: 'company' | 'prime' | 'shared' | 'admin'
): ComponentInfo[] => {
  return Object.values(COMPONENT_REGISTRY).filter((comp) => comp.domain === domain);
};

/**
 * Get component by name
 */
export const getComponent = (name: string): React.ComponentType<any> | undefined => {
  return COMPONENT_REGISTRY[name]?.component;
};

/**
 * Get all company artifact components
 */
export const getCompanyArtifactComponents = (): ComponentInfo[] => {
  return getComponentsByDomain('company');
};

/**
 * Get all prime artifact components
 */
export const getPrimeArtifactComponents = (): ComponentInfo[] => {
  return getComponentsByDomain('prime');
};

/**
 * Validate component registry
 */
export const validateComponentRegistry = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  Object.entries(COMPONENT_REGISTRY).forEach(([key, info]) => {
    if (!info.name) errors.push(`Component ${key} missing name`);
    if (!info.component) errors.push(`Component ${key} missing component`);
    if (!info.domain) errors.push(`Component ${key} missing domain`);
    if (!info.description) errors.push(`Component ${key} missing description`);
    if (!info.filePath) errors.push(`Component ${key} missing filePath`);

    // Validate domain values
    if (!['company', 'prime', 'shared', 'admin'].includes(info.domain)) {
      errors.push(`Component ${key} has invalid domain: ${info.domain}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
